// eslint-disable-next-line
import React, { FC, createContext, useContext, useMemo } from 'react';
import { useImmerReducer } from 'use-immer';
import { Nullable, ValueOf, Overwrite } from 'utils/types';
import { actions, types, WizardActions } from './actions';
import { bindActionCreators } from './utils';

export type StepIndex = string;
export type StepState = {};

export type ModalType = 'EXIT_PROCESS' | 'CHANGE_STEP';

export enum ModalResponseReason {
  CANCEL = 'CANCEL',
  CONFIRM = 'CONFIRM',
}

type WizardModal<K = any> = {
  type: K | undefined;
  title: string;
  content: string;
  show: boolean;
  nextStep: number;
};

type ModalTypes = { [key in ModalType]: WizardModal<key> };

export const initialModalTemplates: ModalTypes = {
  EXIT_PROCESS: {
    type: 'EXIT_PROCESS',
    title: '¿Realmente deseas cancelar?',
    content: 'Al cancelar se perderán todos los datos no guardados.',
    show: false,
    nextStep: 0,
  },
  CHANGE_STEP: {
    type: 'CHANGE_STEP',
    title: '¿Estás seguro que querés editar este paso?',
    content: 'Al aceptar se perderán todos los datos no guardados.',
    show: false,
    nextStep: 0,
  },
};

type WizardModalsProps = {
  [key in ModalType]: Pick<WizardModal<key>, 'title' | 'content'>;
};

type ActionProps = typeof actions;

export type DispatchProps<TState = any> = Overwrite<
  ActionProps,
  {
    saveStepState: <T extends TState, TKey extends keyof T>(
      stepState: Nullable<T[TKey]>,
      step: TKey
    ) => void;
    patchStepState: <T extends TState, TKey extends keyof T>(
      stepState: Partial<T[TKey]>,
      step: TKey
    ) => void;
  }
>;

export type WizardStateProps<T = any> = {
  currentStep: number;
  stepsCount: number;
  stepsState: T;
  isLastStep: boolean;
  showNextButton: boolean;
  showPreviousButton: boolean;
  currentModal: WizardModal<ValueOf<ModalType>>;
  flowCompleted: boolean;
  modalTemplates: ModalTypes;
  historyCancel: string;
};

type ContextProps = [WizardStateProps, DispatchProps];

const StateContext = createContext<undefined | ContextProps>(undefined);

export const _initialState = {
  currentStep: 0,
  stepsCount: 0,
  stepsState: {},
  isLastStep: false,
  showNextButton: true,
  showPreviousButton: false,
  modalTemplates: initialModalTemplates,
  currentModal: {
    type: undefined,
    show: false,
    title: '',
    content: '',
    nextStep: 0,
  },
  flowCompleted: false,
  historyCancel: '/dashboard',
};

const reducer = (draftState: WizardStateProps, action: WizardActions) => {
  switch (action.type) {
    case types.wNext:
      draftState.currentStep += 1;
      break;
    case types.wPrev:
      draftState.currentStep -= 1;
      break;
    case types.wSaveStepState:
      draftState.stepsState[action.payload.step] = action.payload.stepState;
      break;
    case types.wPatchStepState:
      const newState = Object.keys(action.payload.stepState).reduce(
        (accumulator) => {
          accumulator = { ...accumulator, ...action.payload.stepState };
          return accumulator;
        },
        draftState.stepsState[action.payload.step]
      );
      draftState.stepsState[action.payload.step] = newState;
      break;
    case types.wSetStepsCount:
      draftState.stepsCount = action.payload;
      break;
    case types.wOpenModal: {
      Object.assign(draftState.currentModal, {
        ...initialModalTemplates[action.payload.type],
        ...draftState.modalTemplates[action.payload.type],
      });

      draftState.currentModal.show = true;

      if (action.payload.nextStep) {
        draftState.currentModal.nextStep = action.payload.nextStep;
      }
      break;
    }
    case types.wCloseModal:
      draftState.currentModal.show = false;
      break;
    case types.wSetFlowCompleted:
      draftState.flowCompleted = action.payload;
      break;
    case types.wGoToStep:
      draftState.currentStep = action.payload;
      break;
    default:
      throw new Error(`Unhandled action `);
  }
};

const WizardProvider: FC<{
  initialState?: WizardStateProps;
  modalTemplates?: Partial<WizardModalsProps>;
  historyCancel?: string;
}> = ({
  children,
  initialState = _initialState,
  modalTemplates = _initialState.modalTemplates,
  historyCancel = _initialState.historyCancel,
}) => {
  const [state, dispatcher] = useImmerReducer(reducer, {
    ...initialState,
    modalTemplates: Object.keys(_initialState.modalTemplates).reduce(
      (acc, k) => {
        acc[k] = {
          ..._initialState.modalTemplates[k],
          ...modalTemplates[k],
        };
        return acc;
      },
      {} as ModalTypes
    ),
    historyCancel,
  });
  const dispatch = useMemo(() => bindActionCreators(actions, dispatcher), [
    dispatcher,
  ]) as DispatchProps;

  const { currentStep, stepsCount } = state;
  const showPreviousButton = currentStep > 0;
  const showNextButton = currentStep < stepsCount;
  const isLastStep = currentStep === stepsCount;

  const finalState = {
    ...state,
    showPreviousButton,
    showNextButton,
    isLastStep,
  };

  return (
    <StateContext.Provider value={[finalState, dispatch]}>
      {children}
    </StateContext.Provider>
  );
};

const withWizardProvider = <P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    initialState?: WizardStateProps;
    modalTemplates?: Partial<WizardModalsProps>;
    historyCancel?: string;
  }
) => {
  return (props: any) => (
    <WizardProvider {...options}>
      <Component {...(props as P)} />
    </WizardProvider>
  );
};

function useWizard<T = StepState>(): [WizardStateProps<T>, DispatchProps<T>] {
  const context = useContext(StateContext);
  if (context === undefined) {
    throw new Error('useWizardContext must be used within a WizardProvider');
  }
  return context;
}

function useWizardStateStep<TState = StepState, TSelected = unknown>(
  selector: (state: TState) => TSelected
): TSelected {
  const [wizardState] = useWizard<TState>();
  return selector(wizardState.stepsState);
}

export { WizardProvider, withWizardProvider, useWizard, useWizardStateStep };
